import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { updatePassword } = useContext(AuthContext);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    const handleRecoveryToken = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');

      if (accessToken && type === 'recovery') {
        try {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: hashParams.get('refresh_token') || accessToken,
          });

          if (sessionError) {
            console.error('Session error:', sessionError);
            setTokenValid(false);
            setError('This reset link has expired or is invalid. Please request a new one.');
          } else {
            setTokenValid(true);
            window.history.replaceState(null, '', window.location.pathname);
          }
        } catch (err) {
          console.error('Token handling error:', err);
          setTokenValid(false);
          setError('This reset link has expired or is invalid. Please request a new one.');
        }
      } else {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setError('No valid reset link found. Please request a new password reset.');
        }
      }
    };

    handleRecoveryToken();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || !confirm) {
      setError('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    const result = await updatePassword(password);
    setLoading(false);

    if (result.success) {
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } else {
      setError(result.error || 'Something went wrong. Please try again.');
    }
  };

  if (tokenValid === null) {
    return (
      <div className="mobile-container bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-container bg-white flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex flex-col px-6 pt-12 pb-8"
      >
        <div className="mb-8">
          <img
            src="https://cloud.sharemyimage.com/2026/08/16/App-Icon.png"
            alt="DayClap"
            className="w-28 h-28 rounded-3xl mb-5 shadow-sm"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Set new password</h1>
          <p className="text-gray-500">Enter your new password below. Must be at least 6 characters.</p>
        </div>

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-100 p-6 rounded-2xl text-center"
          >
            <div className="text-4xl mb-3">✅</div>
            <h3 className="font-bold text-green-800 mb-2">Password updated!</h3>
            <p className="text-sm text-green-600">Redirecting you to sign in...</p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
              <div className="relative">
                <SafeIcon name="Lock" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full pl-12 pr-12 py-4 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  <SafeIcon name={showPassword ? 'EyeOff' : 'Eye'} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
              <div className="relative">
                <SafeIcon name="Lock" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat your new password"
                  className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                />
              </div>
              {password && confirm && password === confirm && (
                <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                  <SafeIcon name="Check" className="text-xs" /> Passwords match
                </p>
              )}
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="bg-red-50 border border-red-100 text-red-600 text-sm p-3 rounded-xl flex items-center gap-2"
              >
                <SafeIcon name="AlertCircle" />
                {error}
              </motion.div>
            )}

            {!tokenValid && (
              <div className="text-center">
                <a
                  href="/forgot-password"
                  className="text-sm text-pink-500 font-semibold hover:text-pink-600"
                >
                  Request a new reset link →
                </a>
              </div>
            )}

            {tokenValid && (
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200 hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </button>
            )}
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPassword;